import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Button,
    Card,
    Popconfirm,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { bdoApi } from '../../../api/client';
import CollectivePackagingModal from './CollectivePackagingModal';
import type { BdoCollectiveEntry, BdoRange } from '../types';
import { MATERIAL_LABELS, formatGrams, formatMg } from '../types';

const { Text } = Typography;

interface Props {
    range: BdoRange;
    isMobile?: boolean;
}

export default function CollectivePackagingTab({ range, isMobile }: Props) {
    const queryClient = useQueryClient();
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<BdoCollectiveEntry | null>(null);

    const { data = [], isLoading } = useQuery({
        queryKey: ['bdo-collective', range.year, range.from, range.to],
        queryFn: () =>
            bdoApi
                .getCollectivePackaging({
                    year: range.year,
                    from: range.from,
                    to: range.to,
                })
                .then((res) => res.data),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => bdoApi.deleteCollectivePackaging(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bdo-collective'] });
            queryClient.invalidateQueries({ queryKey: ['bdo-summary'] });
            message.success('Wpis usunięty');
        },
        onError: () => message.error('Nie udało się usunąć wpisu'),
    });

    const columns = [
        {
            title: 'Data',
            dataIndex: 'date',
            key: 'date',
            width: 110,
            render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
        },
        {
            title: 'Materiał',
            dataIndex: 'material',
            key: 'material',
            render: (m: keyof typeof MATERIAL_LABELS) => (
                <Tag color="blue">{MATERIAL_LABELS[m] || m}</Tag>
            ),
        },
        {
            title: 'Masa [g]',
            dataIndex: 'massGrams',
            key: 'massGrams',
            align: 'right' as const,
            render: (v: number | string) => formatGrams(v),
        },
        {
            title: 'Masa [Mg]',
            dataIndex: 'massGrams',
            key: 'massMg',
            align: 'right' as const,
            render: (v: number | string) => <Text strong>{formatMg(Number(v) / 1_000_000)}</Text>,
        },
        {
            title: 'Opis',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (v: string) => v || '—',
        },
        {
            title: 'Autor',
            key: 'author',
            width: 160,
            render: (_: any, record: BdoCollectiveEntry) =>
                record.createdBy
                    ? `${record.createdBy.firstName} ${record.createdBy.lastName}`
                    : '—',
        },
        {
            title: 'Akcje',
            key: 'actions',
            width: 90,
            render: (_: any, record: BdoCollectiveEntry) => (
                <Space size={4}>
                    <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditing(record);
                            setModalOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="Usunąć wpis?"
                        okText="Usuń"
                        cancelText="Anuluj"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteMutation.mutate(record.id)}
                    >
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            size="small"
            title="Opakowania zbiorcze/transportowe"
            extra={
                <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditing(null);
                        setModalOpen(true);
                    }}
                >
                    Dodaj
                </Button>
            }
        >
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                size={isMobile ? 'small' : 'middle'}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                scroll={{ x: 820 }}
            />

            <CollectivePackagingModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setEditing(null);
                }}
                entry={editing}
                defaultDate={(() => {
                    const today = dayjs().format('YYYY-MM-DD');
                    return today >= range.from && today <= range.to ? today : range.to;
                })()}
            />
        </Card>
    );
}
